import { queryOptions } from '@tanstack/react-query';
import { listBookings, getBooking } from '@/api/generated/sdk.gen';
import type { BookingSummary, ListBookingsResponse, BookingDetail, BookingRevision } from '@/api/generated/types.gen';

export type { BookingSummary, ListBookingsResponse, BookingDetail, BookingRevision };

/**
 * A booking WE own and can deterministically cancel (ADR-034): not in Channex
 * (`channex_booking_id` null), direct/manual source, and `confirmed`.
 *
 * CONFIRMED-ONLY is deliberate: an ARI-only direct booking is created
 * `confirmed`, so this covers the real case — no live writer can produce a
 * `pending` direct/manual row (InsertConfirmedDirect/InsertConfirmedManual both
 * hardcode `confirmed`); the guard here is a safety net for any other status.
 * OTA/Channex bookings (channex id set) are cancelled in Channex, not here. The
 * UI also gates the Cancel control on the `bookings.cancel` permission.
 */
export function isCancelEligible(
  b: Pick<BookingDetail, 'channex_booking_id' | 'source' | 'status'>,
): boolean {
  return (
    !b.channex_booking_id &&
    (b.source === 'direct' || b.source === 'manual') &&
    b.status === 'confirmed'
  );
}

/**
 * A booking WE own (spec §4.8) whose checkout date the operator may move via
 * `POST .../change-checkout`: same eligibility as {@link isCancelEligible} —
 * not in Channex, direct/manual source, confirmed — named for its own
 * purpose rather than reused under cancel's name. The server re-checks the
 * same three facts (plus `expected_version`); this only gates the button.
 */
export function isCheckoutEditable(
  b: Pick<BookingDetail, 'channex_booking_id' | 'source' | 'status'>,
): boolean {
  return (
    !b.channex_booking_id &&
    (b.source === 'direct' || b.source === 'manual') &&
    b.status === 'confirmed'
  );
}

/** Money rows derived from a booking, in minor units of its currency. */
export interface BookingMoney {
  /** What the OTA takes. null = unknown, or a cancelled booking that owes none. */
  commission: number | null;
  /**
   * Tax the OTA added on top of the room price and did NOT collect — we take it
   * from the guest on arrival and remit it. Outside `total_amount_minor`, so it
   * is shown as its own row and never enters the payout. null = nothing to show.
   */
  taxOnTop: number | null;
  /** What stays with us: total − commission. null whenever `commission` is. */
  payout: number | null;
}

/**
 * The single definition of a booking's money rows, shared by the booking card
 * and the drawer. Duplicating this arithmetic is what let the payout silently
 * count tourism tax as income (OH-123) — it was fixed in one of the two copies
 * of an earlier bug and drifted from there.
 *
 * Commission and payout render as a pair or not at all: a payout row without
 * the commission that produced it is indistinguishable from the total. A
 * cancelled booking stores a genuine 0, so it needs excluding explicitly —
 * "€0.00 commission, €274.20 payout" would promise money nobody will receive.
 */
export function bookingMoney(
  b: Pick<
    BookingDetail,
    'status' | 'total_amount_minor' | 'ota_commission_minor' | 'taxes_excl_minor'
  >,
): BookingMoney {
  if (b.status === 'cancelled') {
    return { commission: null, taxOnTop: null, payout: null };
  }
  const commission = b.ota_commission_minor ?? null;
  const tax = b.taxes_excl_minor ?? null;
  return {
    commission,
    taxOnTop: tax !== null && tax > 0 ? tax : null,
    payout: commission === null ? null : b.total_amount_minor - commission,
  };
}

export function bookingQuery(id: string) {
  return queryOptions({
    queryKey: ['booking', id] as const,
    queryFn: async ({ signal }) => {
      const res = await getBooking({ throwOnError: true, signal, path: { id } });
      return res.data;
    },
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      // Don't retry on 401/404
      return status !== 401 && status !== 404 && count < 1;
    },
    staleTime: 30_000,
  });
}

export interface BookingsFilter {
  status?: string[];
  apartmentIds?: string[];
  checkinFrom?: string;   // YYYY-MM-DD
  checkinTo?: string;
  source?: string[];
  limit: number;
  offset: number;
}

export type BookingTab =
  | 'all'
  | 'upcoming'
  | 'inhouse'
  | 'past'
  | 'cancelled';

export const BOOKING_TABS: { value: BookingTab; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'inhouse', label: 'In-house' },
  { value: 'past', label: 'Past' },
  { value: 'cancelled', label: 'Cancelled' },
];

// tabToFilter maps a bookings tab to its server-side status filter (empty =
// no status filter); the status tabs partition the booking lifecycle.
// (no_show is folded into Past — a guest that never showed is a finished,
// past booking.) A "Stuck" tab used to live here (stuck direct-booking
// markers, via the now-removed `stuck` list filter) — proven dead (no live
// writer of `bookings` can produce a stuck marker row) and removed; see
// docs/09-decisions.md's ADR-034 amendment.
export function tabToFilter(tab: BookingTab): { status: string[] } {
  switch (tab) {
    case 'upcoming':
      return { status: ['confirmed', 'pending'] };
    case 'inhouse':
      return { status: ['checked_in'] };
    case 'past':
      return { status: ['checked_out', 'no_show'] };
    case 'cancelled':
      return { status: ['cancelled'] };
    case 'all':
    default:
      return { status: [] };
  }
}

export function bookingsQuery(filter: BookingsFilter) {
  // Stable queryKey: sort array filters so {a,b} and {b,a} hit the same cache entry.
  const normalised = {
    ...filter,
    status: filter.status ? [...filter.status].sort() : undefined,
    source: filter.source ? [...filter.source].sort() : undefined,
    apartmentIds: filter.apartmentIds ? [...filter.apartmentIds].sort() : undefined,
  };
  return queryOptions({
    queryKey: ['bookings', normalised] as const,
    queryFn: async ({ signal }) => {
      const res = await listBookings({
        throwOnError: true,
        signal,
        query: {
          status: filter.status?.length
            ? (filter.status as ('pending' | 'confirmed' | 'cancelled' | 'no_show' | 'checked_in' | 'checked_out')[])
            : undefined,
          apartment_ids: filter.apartmentIds?.length ? filter.apartmentIds : undefined,
          checkin_from: filter.checkinFrom || undefined,
          checkin_to: filter.checkinTo || undefined,
          source: filter.source?.length
            ? (filter.source as ('direct' | 'booking_com' | 'airbnb' | 'expedia' | 'manual')[])
            : undefined,
          limit: filter.limit,
          offset: filter.offset,
        },
      });
      return res.data;
    },
    retry: (count, err) => {
      const status = (err as { status?: number })?.status;
      return status !== 401 && count < 1;
    },
    staleTime: 30_000,
  });
}
